import { useState, useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

export interface AdminUser {
  id: string;
  uid: string;
  email: string;
  displayName: string | null;
  role: 'shopper' | 'business' | 'driver' | 'admin';
  status: 'active' | 'suspended';
  createdAt: Timestamp | Date;
  lastActive?: Timestamp | Date;
  ordersCount?: number;
  totalSpent?: number;
  suspendedAt?: Timestamp;
  suspendedReason?: string;
}

export function useAdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'users'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => {
          const raw = d.data();
          return {
            id: d.id,
            uid: raw.uid || d.id,
            email: raw.email || '',
            displayName: raw.displayName || null,
            role: raw.role || 'shopper',
            status: raw.status || 'active',
            createdAt: raw.createdAt || new Date(),
            lastActive: raw.lastActive,
            ordersCount: raw.ordersCount || 0,
            totalSpent: raw.totalSpent || 0,
            suspendedAt: raw.suspendedAt,
            suspendedReason: raw.suspendedReason,
          } as AdminUser;
        });
        setUsers(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching users for admin:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { users, loading, error };
}

/**
 * Suspend or activate a user via Cloud Functions.
 * The Cloud Function handles disabling/enabling Firebase Auth
 * and updating the Firestore document + audit log.
 */
export async function setUserStatus(
  user: AdminUser,
  suspended: boolean,
  reason?: string
): Promise<void> {
  if (suspended) {
    const suspend = httpsCallable(functions, 'suspendUser');
    await suspend({ userId: user.id, reason: reason || 'Suspended by admin' });
  } else {
    const activate = httpsCallable(functions, 'activateUser');
    await activate({ userId: user.id });
  }
}
